// 📄 pages/Admin/sections/BadiCatalogSection.tsx
import { useEffect, useRef, useState } from 'react';
import { Plus, RefreshCw, DatabaseZap, Sparkles } from 'lucide-react';
import { BadiObjeto, getBadis, deleteBadi, seedCatalogoBadis } from '../../../services/catalogoBadisService.ts';
import BadiCatalogGrid from '../../../components/abap/BadiCatalogGrid.tsx';
import BadiFormModal from '../components/BadiFormModal.tsx';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';

export default function BadiCatalogSection() {
  const [badis, setBadis] = useState<BadiObjeto[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingBadi, setEditingBadi] = useState<BadiObjeto | null>(null);
  const [badiAEliminar, setBadiAEliminar] = useState<BadiObjeto | null>(null);
  const [eliminando, setEliminando] = useState(false);
  const [sembrando, setSembrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El Admin ya tiene permiso de escritura garantizado (ruta gateada por
  // AdminRoute), así que acá el auto-seed es siempre seguro. `useRef` en
  // vez de un booleano en el catálogo mismo, para no reintentar en cada
  // re-render ni duplicar el intento si el efecto corre dos veces
  // (StrictMode) — seedCatalogoBadis ya es idempotente igual, pero evita
  // dos escrituras innecesarias en paralelo.
  const seedIntentado = useRef(false);

  const cargar = async () => {
    setLoading(true);
    try {
      let data = await getBadis();
      if (data.length === 0 && !seedIntentado.current) {
        seedIntentado.current = true;
        setSembrando(true);
        try {
          await seedCatalogoBadis();
          data = await getBadis();
        } finally {
          setSembrando(false);
        }
      }
      setBadis(data);
    } catch (err) {
      console.error('Error cargando el catálogo de BAdIs:', err);
      setError('No se pudo cargar el catálogo de BAdIs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const openCreate = () => { setEditingBadi(null); setModalOpen(true); };
  const openEdit = (badi: BadiObjeto) => { setEditingBadi(badi); setModalOpen(true); };
  const handleSaved = () => { setModalOpen(false); cargar(); };

  const handleEliminar = async () => {
    if (!badiAEliminar) return;
    setEliminando(true);
    try {
      await deleteBadi(badiAEliminar.id);
      setBadiAEliminar(null);
      cargar();
    } catch (err) {
      console.error('Error al eliminar el objeto:', err);
    } finally {
      setEliminando(false);
    }
  };

  const handleSeed = async () => {
    setSembrando(true);
    setError(null);
    try {
      await seedCatalogoBadis();
      cargar();
    } catch (err: any) {
      setError(err.message || 'Error al cargar los datos de ejemplo.');
    } finally {
      setSembrando(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-light text-white">Catálogo de BAdIs y Objetos ABAP</h1>
          <p className="text-sm text-gray-400 mt-1">BAdIs estándar, implementaciones Z, clases, programas e interfaces de las integraciones SAP Ariba / S/4HANA.</p>
        </div>
        <div className="flex items-center gap-2">
          {badis.length === 0 && !loading && (
            <button
              onClick={handleSeed}
              disabled={sembrando}
              className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-50 px-4 py-2.5 rounded-xl text-sm font-semibold text-gray-300 transition-all"
            >
              <Sparkles className="w-4 h-4" />
              {sembrando ? 'Cargando...' : 'Cargar datos de ejemplo'}
            </button>
          )}
          <button
            onClick={openCreate}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 px-4 py-2.5 rounded-xl text-sm font-semibold text-[#fff] transition-all shadow-lg"
          >
            <Plus className="w-4 h-4" />
            Nuevo Objeto
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-500" />
          <p className="text-sm">{sembrando ? 'Inicializando catálogo con datos de ejemplo...' : 'Cargando catálogo...'}</p>
        </div>
      ) : badis.length === 0 ? (
        <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
          <DatabaseZap className="w-8 h-8 mx-auto mb-2 text-gray-600" />
          <p className="text-sm">Aún no hay objetos registrados en el catálogo.</p>
        </div>
      ) : (
        <BadiCatalogGrid badis={badis} onEdit={openEdit} onDelete={setBadiAEliminar} />
      )}

      {modalOpen && (
        <BadiFormModal badi={editingBadi} onClose={() => setModalOpen(false)} onSaved={handleSaved} />
      )}

      {badiAEliminar && (
        <ConfirmDialog
          title="¿Eliminar este objeto del catálogo?"
          message={`"${badiAEliminar.nombre}" se eliminará del catálogo global. Esta acción no se puede deshacer.`}
          confirmLabel={eliminando ? 'Eliminando...' : 'Eliminar'}
          danger
          onConfirm={handleEliminar}
          onCancel={() => setBadiAEliminar(null)}
        />
      )}
    </div>
  );
}
